// oxlint-disable sorare/no-unused-fragment-fields
import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import { isFuture } from 'date-fns';
import type { ReactNode } from 'react';
import { useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import {
  AverageLastScoreLocation,
  GameStatus,
  ScoreStatus,
  Sport,
} from '@sorare/core/src/__generated__/globalTypes';
import { Skeleton } from '@sorare/core/src/atoms/animations/Skeleton';
import type { IconDefinition } from '@sorare/core/src/atoms/icons';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { iconCalendar } from '@sorare/core/src/atoms/icons/system/iconCalendar.ts';
import { iconCalendarXmark } from '@sorare/core/src/atoms/icons/system/iconCalendarXmark.ts';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { HeadlineXS, LabelS } from '@sorare/core/src/atoms/typography';
import { TeamAvatar } from '@sorare/core/src/components/club/TeamAvatar';
import { FixtureChart as DumbFixtureChart } from '@sorare/core/src/components/player/FixtureChart';
import { StatIcon } from '@sorare/core/src/components/stats/StatIcon';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { idFromObject } from '@sorare/core/src/gql/idFromObject';
import { useIsMobileApp } from '@sorare/core/src/hooks/useIsMobileApp';
import { useRetainedValue } from '@sorare/core/src/hooks/useRetainedValue';
import { groupBy, range } from '@sorare/core/src/lib/arrays';
import { sortByDate } from '@sorare/core/src/lib/dates';
import {
  fantasy,
  gameStatusMessages,
  playerDetails,
  playerGameStatusLabels,
} from '@sorare/core/src/lib/glossary';
import {
  negativeDecisiveStatKeys,
  positiveDecisiveStatKeys,
} from '@sorare/core/src/lib/stats';
import { findThresholdColor } from '@sorare/core/src/lib/thresholds';
import { css } from '@sorare/css';

import type { Filters as FiltersType, Options } from '#src/lib/performance.tsx';
import { ALL_VALUE, FilterKey } from '#src/lib/performance.tsx';
import { PlayerUnavailabilityBadge } from '#src/shared/player/PlayerUnavailabilityBadge/index.tsx';
import { useGetSuspensionsAndInjuries } from '#src/shared/player/useGetSuspensionsAndInjuries.ts';
import { PerformanceFilters } from '#src/shared/stats/PerformanceFilters/index.tsx';
import { PlayerGameScoreDialog } from '#src/shared/stats/PlayerGameScoreDialog/index.tsx';
import { PlayerGameScoreStatusIcon } from '#src/shared/stats/PlayerGameScoreStatusIcon/index.tsx';
import { usePerformanceFilters } from '#src/shared/stats/hooks/usePerformanceFilters.tsx';
import { getDecisiveActions } from '#src/shared/stats/lib.tsx';

import { FixtureBarProjections } from './FixtureBarProjections/index.tsx';
import type {
  FixtureChart_anyCard,
  FixtureChart_anyPlayer,
  FixtureChart_playerGameScoreInterface,
} from './__generated__/index.graphql.ts';
import { useGetFiltersOptions } from './useGetFiltersOptions.tsx';

export type Props = {
  card?: FixtureChart_anyCard;
  so5Scores: (FixtureChart_playerGameScoreInterface | null)[];
  player: Nullable<FixtureChart_anyPlayer>;
  InfiniteScrollLoader?: ReactNode;
  onScoreSelect?: ({
    scoreId,
    gameId,
  }: {
    scoreId: string;
    gameId: string;
  }) => void;
  disableScoreSelect?: boolean;
  hideTitle?: boolean;
  inDrawer?: boolean;
  filters?: FiltersType;
  filtersNode?: ReactNode;
  onFilterChange?: (key: FilterKey, value: FiltersType[FilterKey]) => void;
  options?: Options;
  showUpsell?: boolean;
  disableLinks?: boolean;
  stickyFilters?: boolean;
  loading?: boolean;
  initialTeam?: string;
};

const isHomeGame = (
  gameStats: FixtureChart_playerGameScoreInterface['anyPlayerGameStats']
): boolean => {
  return gameStats.anyTeam.slug === gameStats.anyGame.homeTeam?.slug;
};

const gameOpponent = (
  gameStats: FixtureChart_playerGameScoreInterface['anyPlayerGameStats']
):
  | FixtureChart_playerGameScoreInterface['anyPlayerGameStats']['anyGame']['homeTeam']
  | FixtureChart_playerGameScoreInterface['anyPlayerGameStats']['anyGame']['awayTeam']
  | null => {
  return isHomeGame(gameStats)
    ? gameStats.anyGame.awayTeam
    : gameStats.anyGame.homeTeam;
};

const avatar = css`
  margin: auto;
  height: 20px;
  max-width: 20px;
  --size: 20px;
`;
const gameWeekLabel = css`
  word-spacing: var(--100vw);
`;

const stickyFiltersRow = css`
  background-color: var(--c-surface-background);
  padding: var(--intermediate-unit) 0;
  position: sticky;
  top: var(--current-stack-height);
  z-index: 1;
`;

const gamesContainer = css`
  position: relative;
  mask-image: linear-gradient(to right, transparent 0%, black 10%);
`;

const chartSkeleton = css`
  width: 100%;
  height: 404px;
`;

const filtersSkeleton = css`
  width: 33%;
  height: var(--triple-unit);
`;

const StatusIcon: {
  [key in GameStatus]?: IconDefinition;
} = {
  [GameStatus.postponed]: iconCalendar,
  [GameStatus.cancelled]: iconCalendarXmark,
};

export const FixtureChart = ({
  so5Scores,
  player,
  card,
  onScoreSelect,
  InfiniteScrollLoader,
  hideTitle,
  disableScoreSelect = false,
  inDrawer,
  filters: filtersFromProps,
  onFilterChange: onFilterChangeFromProps,
  options: optionsFromProps,
  showUpsell = true,
  disableLinks,
  stickyFilters,
  loading,
  initialTeam,
}: Props) => {
  const { sport } = useSportContext();
  const { isIosApp } = useIsMobileApp();
  const { formatMessage, formatDate } = useIntl();
  const [playerGameScore, setPlayerGameScore] = useState<
    FixtureChart_playerGameScoreInterface | undefined
  >();
  const retainedPlayerGameScore = useRetainedValue(playerGameScore ?? null);
  const { filters: performanceFilters, handleFilterChange } =
    usePerformanceFilters(player, card, initialTeam);

  const filters = filtersFromProps || performanceFilters;
  const onFilterChange = onFilterChangeFromProps || handleFilterChange;

  const {
    competition: selectedCompetition,
    team: selectedTeam,
    homeAway: selectedHomeAway,
  } = filters;

  const getSuspensionsAndInjuries = useGetSuspensionsAndInjuries(player);
  const maxScore = useMemo(() => {
    if (sport === Sport.FOOTBALL) {
      return 100;
    }
    return undefined;
  }, [sport]);

  const deduplicatedScores = so5Scores
    .filter(Boolean)
    .filter(
      (score, index, self) =>
        index ===
        self.findIndex(
          t =>
            t.anyPlayerGameStats.anyGame.id ===
            score.anyPlayerGameStats.anyGame.id
        )
    );

  const opts = useGetFiltersOptions({
    so5Scores: deduplicatedScores,
    player,
    card,
  });
  const options = optionsFromProps || opts;

  const {
    [FilterKey.COMPETITION]: competitionsOptions,
    [FilterKey.TEAM]: teamsOptions,
  } = options;

  if (player && !loading && !deduplicatedScores?.length) return null;

  const canFilterTeam = teamsOptions.length > 2;

  const canFilterCompetition = competitionsOptions.length > 2;

  const sortedScores = sortByDate(
    deduplicatedScores,
    score => score.anyGame.date
  );

  const sortedGames = groupBy(sortedScores, t => {
    const { date } = t.anyGame;
    return new Date(date.getFullYear(), date.getMonth(), 1).toString();
  });

  const normalized = Object.keys(sortedGames).map((key: string) => {
    return {
      key,
      label: formatDate(key, { month: 'short', year: '2-digit' }),
      games: (sortedGames[key] ?? []).map(score => {
        const { status } = score.anyPlayerGameStats.anyGame;
        const gamePlayed =
          status !== GameStatus.postponed && status !== GameStatus.cancelled;
        const isDNP = score.scoreStatus === ScoreStatus.DID_NOT_PLAY;
        const playerScore =
          !isDNP && gamePlayed && typeof score.score === 'number'
            ? score.score
            : undefined;
        const decisiveActions = getDecisiveActions(score);

        const isHighlighted = (() => {
          if (
            canFilterCompetition &&
            selectedCompetition !== ALL_VALUE &&
            selectedCompetition !==
              score.anyPlayerGameStats.anyGame.competition.slug
          ) {
            return false;
          }
          if (
            canFilterTeam &&
            selectedTeam !== ALL_VALUE &&
            selectedTeam !== score.anyPlayerGameStats.anyTeam.slug
          ) {
            return false;
          }
          if (
            selectedHomeAway !== ALL_VALUE &&
            (selectedHomeAway === AverageLastScoreLocation.AWAY) !==
              !isHomeGame(score.anyPlayerGameStats)
          ) {
            return false;
          }

          return true;
        })();

        const { so5Fixture } = score.anyPlayerGameStats.anyGame;
        const { seasonGameWeek, gameWeek } = so5Fixture || {};
        const upcoming = isFuture(score.anyGame.date);

        return {
          id: score.anyGame.id,
          playerScore: upcoming ? undefined : playerScore,
          startDate: score.anyGame.date,
          upcoming,
          projectionNode: upcoming ? (
            <FixtureBarProjections player={player} score={score} />
          ) : undefined,
          gameLabel: (
            <Vertical center>
              <TeamAvatar
                className={avatar}
                team={gameOpponent(score.anyPlayerGameStats)}
              />
              <PlayerGameScoreStatusIcon size={2} playerGameScore={score} />
              <LabelS
                color="var(--c-onBase-secondary)"
                className={classNames(gameWeekLabel, 'text-center')}
              >
                <FormattedMessage
                  {...fantasy.gw}
                  values={{
                    week: seasonGameWeek || gameWeek,
                  }}
                />
              </LabelS>
            </Vertical>
          ),
          barContent: (
            <Vertical gap={0.5}>
              {positiveDecisiveStatKeys.map(statKey =>
                range(decisiveActions[statKey] || 0).map(i => (
                  <StatIcon key={`${statKey}-${i}`} statKey={statKey} />
                ))
              )}
            </Vertical>
          ),
          aboveBarContent: (
            <Vertical gap={0.5}>
              {negativeDecisiveStatKeys.map(statKey =>
                range(decisiveActions[statKey] || 0).map(i => (
                  <StatIcon key={`${statKey}-${i}`} statKey={statKey} />
                ))
              )}
            </Vertical>
          ),
          tresholdColor: findThresholdColor(
            score.score || 0,
            sport || Sport.FOOTBALL,
            'PLAYER_SCORE'
          ),
          ...(!isIosApp &&
            !disableScoreSelect && {
              onClick: () => {
                if (onScoreSelect) {
                  onScoreSelect({
                    scoreId: idFromObject(score.id),
                    gameId: idFromObject(score.anyGame.id),
                  });
                  return;
                }
                setPlayerGameScore(score);
              },
            }),
          dnpLabel: !gamePlayed ? (
            <FontAwesomeIcon
              icon={StatusIcon[status]!}
              title={formatMessage(gameStatusMessages[status])}
              size="lg"
            />
          ) : (
            <>
              <PlayerUnavailabilityBadge
                {...getSuspensionsAndInjuries<FixtureChart_anyPlayer>(
                  score.anyGame
                )}
              />
              <FormattedMessage
                {...playerGameStatusLabels.did_not_play_short}
              />
            </>
          ),
          isHighlighted,
        };
      }),
    };
  });

  return (
    <>
      {!hideTitle && (
        <HeadlineXS as="h2">
          <FormattedMessage {...playerDetails.performance} />
        </HeadlineXS>
      )}
      {!!filters && !!onFilterChange && (
        <Horizontal
          className={classNames('overflow-x-auto', {
            [stickyFiltersRow]: stickyFilters,
          })}
        >
          {loading ? (
            <Skeleton className={filtersSkeleton} />
          ) : (
            <PerformanceFilters
              filters={filters}
              options={options}
              onFilterChange={onFilterChange}
            />
          )}
        </Horizontal>
      )}
      {loading ? (
        <Skeleton className={chartSkeleton} />
      ) : (
        <div className={gamesContainer}>
          <DumbFixtureChart
            InfiniteScrollLoader={InfiniteScrollLoader}
            fixturesStats={normalized}
            yMaxValue={maxScore}
          />
        </div>
      )}

      <PlayerGameScoreDialog
        playerGameScoreId={retainedPlayerGameScore?.id ?? ''}
        onClose={() => setPlayerGameScore(undefined)}
        open={!!playerGameScore}
        showUpsell={showUpsell}
        inDrawer={inDrawer}
        disableLinks={disableLinks}
      />
    </>
  );
};

FixtureChart.fragments = {
  playerGameScoreInterface: gql`
    fragment FixtureChart_playerGameScoreInterface on PlayerGameScoreInterface {
      id
      score
      scoreStatus
      projectedScore
      anyPlayerGameStats {
        id
        ... on PlayerGameStats {
          id
          minsPlayed
        }
        anyTeam {
          slug
          shortName
          ...TeamAvatar_team
        }
        anyGame {
          id
          competition {
            slug
            displayName
          }
          status: statusTyped
          so5Fixture {
            slug
            gameWeek
            seasonGameWeek
          }
          homeTeam {
            slug
            ...TeamAvatar_team
          }
          awayTeam {
            slug
            ...TeamAvatar_team
          }
        }
      }
      ...useGetSuspensionsAndInjuries_playerGameScoreInterface
      ...PlayerGameScoreStatusIcon_playerGameScoreInterface
      ...getDecisiveActions_playerGameScoreInterface
      ...useGetFiltersOptions_playerGameScoreInterface
      ...FixtureBarProjections_playerGameScoreInterface
    }
    ${TeamAvatar.fragments.team}
    ${useGetSuspensionsAndInjuries.fragments.playerGameScoreInterface}
    ${PlayerGameScoreStatusIcon.fragments.playerGameScoreInterface}
    ${getDecisiveActions.fragments.playerGameScoreInterface}
    ${useGetFiltersOptions.fragments.playerGameScoreInterface}
    ${FixtureBarProjections.fragments.score}
  ` as TypedDocumentNode<FixtureChart_playerGameScoreInterface>,
  anyPlayer: gql`
    fragment FixtureChart_anyPlayer on AnyPlayerInterface {
      slug
      injuries {
        id
        ...PlayerUnavailabilityBadge_injury
      }
      suspensions {
        id
        ...PlayerUnavailabilityBadge_suspension
      }
      ...FixtureBarProjections_anyPlayer
      ...useGetSuspensionsAndInjuries_anyPlayer
      ...useGetFiltersOptions_anyPlayer
      ...usePerformanceFilters_anyPlayer
    }
    ${useGetSuspensionsAndInjuries.fragments.anyPlayer}
    ${PlayerUnavailabilityBadge.fragments.injury}
    ${PlayerUnavailabilityBadge.fragments.suspension}
    ${FixtureBarProjections.fragments.anyPlayer}
    ${useGetFiltersOptions.fragments.anyPlayer}
    ${usePerformanceFilters.fragments.anyPlayer}
  ` as TypedDocumentNode<FixtureChart_anyPlayer>,
  anyCard: gql`
    fragment FixtureChart_anyCard on AnyCardInterface {
      slug
      ...usePerformanceFilters_anyCard
    }
    ${usePerformanceFilters.fragments.anyCard}
  ` as TypedDocumentNode<FixtureChart_anyCard>,
};
